/**
 * DashboardPeopleTab.jsx — Phase 147 (Dashboard Cleanup), extracted from
 * Dashboard.jsx unchanged (People tab body: team size / leaves / attendance
 * / who's-logged-in / monthly late count).
 */
import React from "react";
import { Tag } from "@/components/Layout";
import TeamLeaderboards from "@/components/TeamLeaderboards";
import { Card, TileLink } from "@/components/dashboard/DashboardCards";
import MyGrievancesCard from "@/components/dashboard/MyGrievancesCard";
import TabSection from "@/components/dashboard/TabSection";
import { attendanceTone, attendanceLabel, leaveRemainingTone } from "@/lib/dashboardHelpers";
import { formatTime } from "@/lib/dateFormat";
import { Users, Plane, Clock as ClockIcon, CheckCircle2, LogIn } from "lucide-react";

export default function DashboardPeopleTab({
  activeTab, team, isInternalStaff, pendingLeaves, unloggedHours,
  canFinance, canSeeAllLeaveBalances, allBalances, balance,
  whosLoggedIn, canSeeAttendanceInsights, checkedIn, checkedOut, minutesToday,
  lateCounts,
}) {
  return (
    <TabSection activeTab={activeTab} id="people">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <TileLink to="/my-hr?tab=team"                  label="Team size"      value={team.filter(isInternalStaff).length} icon={Users} testId="tile-team-size" />
        <TileLink to="/my-hr?tab=leaves&status=pending" label="Pending leaves" value={pendingLeaves.length} icon={Plane} accent={pendingLeaves.length > 0 ? "text-orange-700" : ""} testId="tile-pending-leaves" />
        <TileLink to="/my-hr?tab=leaves"                label="Team total unlogged (month)" value={unloggedHours ? `${unloggedHours.total_unlogged_hours}h` : "…"} icon={ClockIcon} testId="tile-unlogged-hours" />
        <TileLink to="/my-hr?tab=team"                  label="Active employees" value={team.filter((u) => isInternalStaff(u) && u.status === "active").length} icon={CheckCircle2} testId="tile-active-employees" />
      </div>

      {/* Phase 84.33 — Removed the standalone "Most leave consumed" Card;
          TeamLeaderboards already renders an equivalent leaderboard for
          admins/CMs so there's no duplication. */}

      {/* Top performers — existing leaderboards */}
      {canFinance && (
        <div className="mt-4">
          <TeamLeaderboards />
        </div>
      )}

      {/* Leave balance (revamped — every team member, remaining balance) + Who's logged in + Attendance + My Grievances */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mt-4">
        <Card title="Leave balance" hint={canSeeAllLeaveBalances ? "Team-wide · remaining" : "This year"} to="/my-hr?tab=leaves" icon={Plane} testId="card-leaves">
          {canSeeAllLeaveBalances && allBalances ? (
            <ul className="space-y-1.5 text-sm max-h-64 overflow-y-auto pr-1" data-testid="leave-balance-team-list">
              {allBalances.users.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((u) => {
                const totalRemaining = u.balances.reduce((s, b) => s + Number(b.remaining || 0), 0);
                return (
                  <li key={u.user_id} className="flex items-center justify-between" data-testid={`leave-balance-row-${u.user_id}`}>
                    <span className="text-slate-700 truncate">{u.name}</span>
                    <span className={`font-mono text-xs ${leaveRemainingTone(totalRemaining)}`}>{totalRemaining} days left</span>
                  </li>
                );
              })}
            </ul>
          ) : balance ? (
            <ul className="space-y-1.5 text-sm">
              {balance.balances.filter((b) => b.entitled > 0).map((b) => (
                <li key={b.leave_type} className="flex items-center justify-between">
                  <span className="text-slate-700 capitalize">{b.leave_type.replace("_", " ")}</span>
                  <span className={`font-mono ${leaveRemainingTone(b.remaining)}`}>{b.taken}/{b.entitled}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-slate-500">Loading…</div>
          )}
        </Card>

        {/* Who's logged in — live, team-wide for admin/founder */}
        <Card title="Who's logged in" hint={canSeeAttendanceInsights ? "Live · team-wide" : "Live"} icon={LogIn} testId="card-whos-logged-in">
          {!whosLoggedIn ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : whosLoggedIn.count === 0 ? (
            <div className="text-sm text-slate-500" data-testid="whos-logged-in-empty">No one is checked in right now.</div>
          ) : (
            <ul className="space-y-1.5 text-sm max-h-64 overflow-y-auto pr-1" data-testid="whos-logged-in-list">
              {whosLoggedIn.rows.map((r) => (
                <li key={r.user_id} className="flex items-center justify-between" data-testid={`whos-logged-in-${r.user_id}`}>
                  <span className="text-slate-700 truncate flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                    {r.user_name}
                  </span>
                  <span className="font-mono text-xs text-slate-500">{formatTime(r.check_in)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Attendance — reused */}
        <Card title="Attendance" hint="Today" to="/my-work?tab=attendance" icon={ClockIcon} testId="card-attendance">
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-700">Status</span>
              <Tag tone={attendanceTone(checkedOut, checkedIn)}>{attendanceLabel(checkedOut, checkedIn)}</Tag>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-700">Time logged</span>
              <span className="font-mono text-slate-900">{Math.floor(minutesToday / 60)}h {minutesToday % 60}m</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Feb 2026 — My Grievances: private, anonymous submission status for THIS employee. */}
      <div className="mt-4">
        <MyGrievancesCard />
      </div>

      {/* Phase 93 — Monthly Late Count: check-ins after 10:30 AM, this month, by person */}
      {canSeeAttendanceInsights && lateCounts && lateCounts.length > 0 && (
        <div className="card-flat overflow-hidden mt-4" data-testid="dashboard-monthly-late-count">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="font-display font-bold text-sm text-slate-900 inline-flex items-center gap-1.5">
              <ClockIcon size={14} className="text-[#F54800]" /> Monthly late count · {new Date().toLocaleString("en-US", { month: "long" })}
            </div>
            <span className="text-[10px] text-slate-400">Check-in after 10:30 AM IST</span>
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {[...lateCounts].sort((a, b) => b.monthly_late_count - a.monthly_late_count).map((r) => (
                  <tr key={r.user_id} className={`border-t border-slate-100 ${r.monthly_late_count >= 3 ? "bg-orange-50/60" : ""}`} data-testid={`dashboard-late-row-${r.user_id}`}>
                    <td className="px-5 py-2 text-slate-800">{r.name}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-slate-800 w-24">{r.monthly_late_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </TabSection>
  );
}
